import type { ReactNode } from "react";

import { resolveFeeApplied } from "@sirdab/returns-core";
import type {
  LookupResponse,
  MoneyHalalas,
  PublicOrderLine,
  PublicPolicy,
} from "@sirdab/returns-shared";
import { Alert } from "@sirdab/ui";

import { failureMessageKey, type ApiFailure } from "../api";
import { ErrorState } from "../components";
import { useFlow, type FlowLineDraft } from "../flow";
import { useOnline } from "../hooks/useOnline";
import { useMessages } from "../i18n";

/**
 * The failure text a screen shows.
 *
 * The server's `reasonText` wins whenever it exists, because a policy decision is the
 * server's to explain and the portal must never author its own version of one
 * (apps/portal/AGENTS.md). Only when there is no server explanation — a dropped
 * connection, a body that failed the schema — does a catalogue key stand in, and then it
 * is deliberately generic rather than a guess at a cause.
 */
export function useFailureText(): (failure: ApiFailure) => string {
  const { t, text } = useMessages();
  return (failure) =>
    failure.kind === "response" && failure.reasonText !== null
      ? text(failure.reasonText)
      : t(failureMessageKey(failure));
}

/** An inline note on the screens that are about to make a network call. */
export function OfflineNote(): React.JSX.Element | null {
  const online = useOnline();
  const { t } = useMessages();
  if (online) return null;
  return <Alert variant="warning">{t("ui.offline")}</Alert>;
}

/**
 * Steps 2-6 are meaningless without the lookup that produced them, and the draft is
 * in-memory only (no browser storage of order data), so a reload legitimately loses it.
 * Rather than render an empty step, say so and offer the way back.
 */
export function RequireLookup({
  children,
}: {
  children: (lookup: LookupResponse) => ReactNode;
}): React.JSX.Element {
  const { lookup, goTo, reset } = useFlow();
  const { t } = useMessages();

  if (lookup === null) {
    return (
      <ErrorState
        message={t("ui.empty_generic")}
        retryLabel={t("action.start_over")}
        onRetry={() => {
          reset();
          goTo("lookup");
        }}
      />
    );
  }
  return <>{children(lookup)}</>;
}

/**
 * The fee figure disclosed at step 5 and again at step 6 (policy check 9).
 *
 * The arithmetic is NOT reimplemented here. `resolveFeeApplied()` from `@sirdab/returns-core` is
 * the same function the server runs to freeze `fee_applied` at creation, so the number the
 * shopper is shown and the number that is stored agree to the halala. All this does is
 * shape the wizard's drafts into `FeeableLine`s: a draft with no reason chosen yet, or one
 * pointing at a line that is not in the order, contributes nothing.
 *
 * The client figure is a disclosure, never a control — the server re-resolves from the
 * policy snapshot (ADR-006).
 */
export function feeTotalHalalas(
  policy: PublicPolicy,
  drafts: readonly FlowLineDraft[],
  orderLines: readonly PublicOrderLine[],
): MoneyHalalas {
  return resolveFeeApplied({
    fees: policy.fees,
    lines: drafts.flatMap((draft) => {
      const line = orderLines.find(
        (candidate) => candidate.lineRef === draft.lineRef,
      );
      if (line === undefined || draft.reasonCode === null) return [];
      return [
        {
          reasonCode: draft.reasonCode,
          unitPrice: line.unitPrice,
          qty: draft.qty,
        },
      ];
    }),
  });
}

/**
 * docs/08 step 5: exchange is withheld for a line whose replacement is out of stock.
 *
 * `replacementInStock` is optional and ABSENT MEANS UNKNOWN, which is read as "offer it".
 * Suppressing an option we cannot substantiate would be the portal inventing a policy
 * decision, which it is not allowed to do — hence `?? true`, not `=== true`.
 */
export function exchangeAvailable(
  drafts: readonly FlowLineDraft[],
  orderLines: readonly PublicOrderLine[],
): boolean {
  return drafts.every((draft) => {
    const line = orderLines.find(
      (candidate) => candidate.lineRef === draft.lineRef,
    );
    return line === undefined || (line.replacementInStock ?? true);
  });
}
